import { useState, type ReactNode } from 'react';
import { Link as RouterLink } from 'react-router';
import { makeStyles, tokens, Text } from '@fluentui/react-components';
import { useT } from '../../i18n';
import { SITE_INFO, type SocialNetwork } from '../../config/siteInfo';
import { PhoneQrDialog } from './PhoneQrDialog';

const useStyles = makeStyles({
  root: {
    width: '100%',
    marginTop: '32px',
    backgroundColor: tokens.colorNeutralBackground1,
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  top: {
    maxWidth: '1100px',
    margin: '0 auto',
    padding: '16px',
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px 24px',
  },
  icons: { display: 'flex', alignItems: 'center', gap: '12px' },
  icon: {
    border: 'none',
    padding: 0,
    cursor: 'pointer',
    display: 'inline-flex',
    width: '32px',
    height: '32px',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '50%',
    color: tokens.colorNeutralForeground2,
    backgroundColor: tokens.colorNeutralBackground3,
    ':hover': { color: tokens.colorBrandForeground1 },
  },
  iconDisabled: { opacity: 0.45, cursor: 'default', ':hover': { color: tokens.colorNeutralForeground2 } },
  link: {
    color: tokens.colorBrandForegroundLink,
    textDecorationLine: 'none',
    ':hover': { textDecorationLine: 'underline' },
  },
  list: { display: 'flex', flexWrap: 'wrap', gap: '8px 16px' },
  strip: {
    backgroundColor: tokens.colorNeutralBackground3,
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  stripInner: {
    maxWidth: '1100px',
    margin: '0 auto',
    padding: '12px 16px',
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '8px 24px',
  },
});

const SOCIAL_ORDER: SocialNetwork[] = ['facebook', 'instagram', 'linkedin', 'youtube', 'x'];

/** Iconos sencillos dibujados con formas básicas (sin dependencias de marca). */
const ICONS: Record<SocialNetwork, ReactNode> = {
  facebook: <text x="12" y="17" textAnchor="middle" fontSize="15" fontWeight="700" fill="currentColor">f</text>,
  instagram: (
    <g fill="none" stroke="currentColor" strokeWidth="1.8">
      <rect x="4" y="4" width="16" height="16" rx="4.5" />
      <circle cx="12" cy="12" r="3.6" />
      <circle cx="17" cy="7" r="0.9" fill="currentColor" stroke="none" />
    </g>
  ),
  linkedin: <text x="12" y="16.5" textAnchor="middle" fontSize="11" fontWeight="700" fill="currentColor">in</text>,
  youtube: (
    <g>
      <rect x="3" y="6.5" width="18" height="11" rx="3.2" fill="none" stroke="currentColor" strokeWidth="1.8" />
      <path d="M10.2 9.6 15 12l-4.8 2.4z" fill="currentColor" />
    </g>
  ),
  x: <path d="M6 6l12 12M18 6 6 18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />,
};

/**
 * Footer global. Se coloca debajo del contenido a pantalla completa, así que solo aparece
 * al llegar al final de la página: redes sociales (iconos siempre visibles; el enlace se
 * activa cuando está configurado), enlace al bot de Telegram y, en el cintillo, las
 * páginas legales y los derechos reservados.
 */
export function SiteFooter({ showBot = false }: { showBot?: boolean }) {
  const s = useStyles();
  const t = useT();
  const f = t.footer;
  const [qrOpen, setQrOpen] = useState(false);

  return (
    <footer className={s.root} data-testid="site-footer">
      <div className={s.top}>
        <div className={s.icons} role="group" aria-label={f.socialTitle}>
          {SOCIAL_ORDER.map((n) => {
            const href = SITE_INFO.social[n];
            const svg = (
              <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">{ICONS[n]}</svg>
            );
            return href ? (
              <a key={n} className={s.icon} href={href} target="_blank" rel="noopener noreferrer" aria-label={f.networks[n]}>
                {svg}
              </a>
            ) : (
              <span key={n} className={`${s.icon} ${s.iconDisabled}`} role="img" aria-label={f.networks[n]}>
                {svg}
              </span>
            );
          })}
          {showBot && (
            <a className={s.icon} href={SITE_INFO.botUrl} target="_blank" rel="noopener noreferrer" aria-label={f.openBot} title={f.openBot}>
              <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
                <g fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round">
                  <rect x="4.5" y="8" width="15" height="11" rx="3" />
                  <path d="M12 8V4.5" strokeLinecap="round" />
                  <circle cx="12" cy="3.5" r="1" fill="currentColor" stroke="none" />
                  <path d="M2.5 12.5v2.5M21.5 12.5v2.5" strokeLinecap="round" />
                </g>
                <circle cx="9" cy="12.5" r="1.4" fill="currentColor" />
                <circle cx="15" cy="12.5" r="1.4" fill="currentColor" />
                <path d="M9.5 16.2h5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
            </a>
          )}
          {showBot && (
            <button type="button" className={s.icon} onClick={() => setQrOpen(true)} aria-label={f.openQr} title={f.openQr}>
              <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
                <g fill="none" stroke="currentColor" strokeWidth="1.8">
                  <rect x="3.5" y="3.5" width="6.5" height="6.5" rx="1" />
                  <rect x="14" y="3.5" width="6.5" height="6.5" rx="1" />
                  <rect x="3.5" y="14" width="6.5" height="6.5" rx="1" />
                </g>
                <g fill="currentColor">
                  <rect x="5.8" y="5.8" width="2" height="2" />
                  <rect x="16.3" y="5.8" width="2" height="2" />
                  <rect x="5.8" y="16.3" width="2" height="2" />
                  <rect x="14" y="14" width="2.4" height="2.4" />
                  <rect x="18.1" y="14" width="2.4" height="2.4" />
                  <rect x="14" y="18.1" width="2.4" height="2.4" />
                  <rect x="18.1" y="18.1" width="2.4" height="2.4" />
                </g>
              </svg>
            </button>
          )}
        </div>
        {showBot && <PhoneQrDialog open={qrOpen} onClose={() => setQrOpen(false)} />}
      </div>

      <div className={s.strip}>
        <div className={s.stripInner}>
          <nav className={s.list} aria-label={f.legalNav}>
            <RouterLink className={s.link} to="/legal/usage">{f.usagePolicy}</RouterLink>
            <RouterLink className={s.link} to="/legal/terms">{f.terms}</RouterLink>
            <RouterLink className={s.link} to="/legal/privacy">{f.privacy}</RouterLink>
          </nav>
          <Text size={200}>{f.rights(new Date().getFullYear())}</Text>
        </div>
      </div>
    </footer>
  );
}
